<template>
  <div>
    <v-btn @click="dialog = true" color="primary">Item Types</v-btn>
    <v-dialog v-model="dialog" persistent width="1100">
      <v-card>
        <v-card-title>Item Types</v-card-title>
        <v-card-text>
          <div class="d-flex">
            <h3 class="mr-2 green--text mt-5">ADD NEW</h3>
            <v-text-field
              class="mr-2"
              label="Item Type Name"
              v-model="form.item_type"
            ></v-text-field>
            <v-text-field
              label="Description"
              v-model="form.description"
            ></v-text-field>
            <v-select
              class="mr-2"
              label="Classification"
              v-model="form.part_classification"
              :items="classificationOptions"
              item-text="text"
              item-value="value"
              clearable
              style="max-width: 200px"
            ></v-select>
            <v-btn color="primary" :loading="loading" @click="add">Add</v-btn>
          </div>
          <v-text-field
            label="Search"
            prepend-icon="mdi-magnify"
            v-model="search"
          ></v-text-field>
          <v-data-table
            :search="search"
            :items="item_types"
            :headers="item_type_headers"
            show-expand
            item-key="id"
          >
            <template v-slot:item.action="{ item }">
              <div class="d-flex align-center">
                <edit-item-type-modal :item="item" />
                <v-btn
                  icon
                  color="red"
                  x-small
                  class="ml-1"
                  @click="deleteType(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:item.part_classification="{ item }">
              <v-chip
                v-if="item.part_classification"
                small
                :color="
                  item.part_classification === 'major' ? 'orange' : 'blue'
                "
                text-color="white"
              >
                {{ item.part_classification === "major" ? "Major" : "Minor" }}
              </v-chip>
              <span v-else class="caption grey--text">Unclassified</span>
            </template>
            <template v-slot:item.items_count="{ item }">
              <v-chip
                small
                :color="item.items_count > 0 ? 'primary' : 'grey lighten-2'"
                :text-color="item.items_count > 0 ? 'white' : 'grey'"
                :class="{ 'clickable-chip': item.items_count > 0 }"
                @click="
                  item.items_count > 0
                    ? showItems(item.item_type, item.items)
                    : null
                "
              >
                {{ item.items_count }}
              </v-chip>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-4 grey lighten-5">
                <div class="mb-3">
                  <h4 class="mb-2">Sub-Types for {{ item.item_type }}</h4>
                  <div class="d-flex align-center mb-3">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      class="mr-2"
                      label="Sub-Type Name"
                      v-model="subTypeForm.item_sub_type"
                      style="max-width: 250px"
                    ></v-text-field>
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      class="mr-2"
                      label="Description"
                      v-model="subTypeForm.description"
                      style="max-width: 250px"
                    ></v-text-field>
                    <v-btn
                      small
                      color="primary"
                      :loading="subTypeLoading"
                      @click="addSubType(item)"
                    >
                      <v-icon left small>mdi-plus</v-icon>Add
                    </v-btn>
                  </div>
                  <v-simple-table
                    v-if="item.sub_types && item.sub_types.length > 0"
                    dense
                  >
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>ID</th>
                        <th>Sub-Type</th>
                        <th>Description</th>
                        <th>Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="st in item.sub_types" :key="st.id">
                        <td>
                          <div class="d-flex align-center">
                            <edit-item-sub-type-modal
                              :subType="st"
                              @updated="refreshTypes"
                            />
                            <v-btn
                              icon
                              color="red"
                              x-small
                              class="ml-1"
                              @click="deleteSubType(st)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </td>
                        <td>{{ st.id }}</td>
                        <td>{{ st.item_sub_type }}</td>
                        <td>{{ st.description || "-" }}</td>
                        <td>
                          <v-chip
                            x-small
                            :color="
                              st.items_count > 0 ? 'primary' : 'grey lighten-2'
                            "
                            :text-color="st.items_count > 0 ? 'white' : 'grey'"
                            :class="{ 'clickable-chip': st.items_count > 0 }"
                            @click="
                              st.items_count > 0
                                ? showItems(st.item_sub_type, st.items)
                                : null
                            "
                          >
                            {{ st.items_count }}
                          </v-chip>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <p v-else class="caption grey--text">No sub-types yet.</p>
                </div>
              </td>
            </template>
          </v-data-table>

          <!-- Items List Dialog -->
          <v-dialog v-model="itemsDialog" max-width="500" scrollable>
            <v-card>
              <v-card-title class="subtitle-1">
                Items in "{{ itemsDialogTitle }}"
                <v-spacer></v-spacer>
                <v-btn icon small @click="itemsDialog = false">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-0" style="max-height: 400px">
                <v-list dense>
                  <v-list-item v-for="itm in itemsDialogList" :key="itm.id">
                    <v-list-item-content>
                      <v-list-item-title class="body-2">{{
                        itm.item_name
                      }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <span class="caption grey--text">ID: {{ itm.id }}</span>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="submit" :loading="loading"
            >Submit</v-btn
          >
          <v-btn @click="dialog = false"> Close</v-btn>
          <v-spacer></v-spacer>
          <p class="caption grey--text">itemTypesModal.vue</p>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import editItemTypeModal from "./editItemTypeModal.vue";
import editItemSubTypeModal from "./editItemSubTypeModal.vue";

export default {
  components: { editItemTypeModal, editItemSubTypeModal },
  data() {
    return {
      search: "",
      form: {},
      subTypeForm: {
        item_sub_type: "",
        description: "",
      },
      subTypeLoading: false,

      classificationOptions: [
        { text: "Major Part", value: "major" },
        { text: "Minor Part", value: "minor" },
      ],
      item_type_headers: [
        { text: "Action", value: "action", width: "100" },
        { text: "ID", value: "id" },
        { text: "Item Type", value: "item_type" },
        { text: "Description", value: "description", width: "300" },
        { text: "Classification", value: "part_classification", width: "150" },
        { text: "Items", value: "items_count", width: "100" },
        { text: "", value: "data-table-expand" },
      ],
      dialog: false,
      loading: false,
      itemsDialog: false,
      itemsDialogTitle: "",
      itemsDialogList: [],
    };
  },
  computed: {
    ...mapGetters({
      item_types: "item_type/item_types",
    }),
  },
  methods: {
    ...mapActions({
      add_item_type: "item_type/add_item_type",
      get_item_types: "item_type/get_item_types",
      delete_item_type: "item_type/delete_item_type",
      add_item_sub_type: "item_sub_type/add_item_sub_type",
      delete_item_sub_type: "item_sub_type/delete_item_sub_type",
    }),
    submit() {
      return;
    },
    async add() {
      let x = await this.$confirm({
        title: "Add Item Type",
        message: "Are you sure you want to proceed?",
        color: "primary",
        icon: "mdi-help-circle-outline",
      });
      if (x) {
        this.loading = true;
        this.add_item_type(this.form);
        this.loading = false;
      }
    },
    async deleteType(item) {
      let msg = "Delete item type '" + item.item_type + "'?";
      if (item.items_count > 0) {
        msg +=
          "<br><br>WARNING: This type has " +
          item.items_count +
          " associated item(s). They will be unassigned from this type.";
      }
      if (item.sub_types && item.sub_types.length > 0) {
        msg += "<br><br>All sub-types will also be deleted.";
      }
      let x = await this.$confirm({
        title: "Delete Item Type",
        message: msg,
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        try {
          await this.delete_item_type(item);
          await this.refreshTypes();
        } catch (err) {
          this.$alert({
            title: "Error",
            message: "Error: " + err,
            color: "error",
            icon: "mdi-alert-circle",
          });
        }
      }
    },
    async addSubType(parentType) {
      if (!this.subTypeForm.item_sub_type) {
        this.$alert({
          title: "Validation",
          message: "Please enter a sub-type name",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      this.subTypeLoading = true;
      try {
        const newSubType = await this.add_item_sub_type({
          item_type_id: parentType.id,
          item_sub_type: this.subTypeForm.item_sub_type,
          description: this.subTypeForm.description,
        });
        // Push into the parent's sub_types array for immediate display
        if (!parentType.sub_types) {
          this.$set(parentType, "sub_types", []);
        }
        parentType.sub_types.push(newSubType);
        this.subTypeForm = { item_sub_type: "", description: "" };
        this.$alert({
          title: "Success",
          message: "Sub-type added successfully",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message: "Error: " + err,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.subTypeLoading = false;
    },
    async deleteSubType(subType) {
      let msg = "Delete sub-type '" + subType.item_sub_type + "'?";
      if (subType.items_count > 0) {
        msg +=
          "<br><br>WARNING: This sub-type has " +
          subType.items_count +
          " associated item(s). They will be unassigned from this sub-type.";
      }
      let x = await this.$confirm({
        title: "Delete Sub-Type",
        message: msg,
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        try {
          await this.delete_item_sub_type(subType);
          await this.refreshTypes();
        } catch (err) {
          this.$alert({
            title: "Error",
            message: "Error: " + err,
            color: "error",
            icon: "mdi-alert-circle",
          });
        }
      }
    },
    async refreshTypes() {
      await this.get_item_types();
    },
    showItems(title, items) {
      this.itemsDialogTitle = title;
      this.itemsDialogList = items || [];
      this.itemsDialog = true;
    },
  },
  async created() {},
};
</script>

<style lang="scss" scoped>
.clickable-chip {
  cursor: pointer;
}
</style>
